import { useEffect, useRef, useState } from 'react';
import { Drawer } from '../organisms/Drawer';
import { Header } from '../molecules/Header';
import { Outlet, useLocation, useNavigate } from 'react-router';
import StickyComponent from '../atoms/StickyComponent';
import { ToastContainer } from '../molecules/ToastMessage';
import { toast } from '../../store/toast';
import { useNavigationGuard } from '../../hooks/useNavigationGuard';

export function Layout() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);

  // R9: toasts are screen-scoped. Clear any still-queued toast when the route changes so a
  // stale validation message (e.g. another part's acceptable range) never renders on top of
  // the next screen and misleads the entry there.
  useEffect(() => {
    if (previousPathRef.current !== location.pathname) {
      previousPathRef.current = location.pathname;
      toast.clear();
    }
  }, [location.pathname]);

  // Registered navigation blockers are enforced here, above the <Outlet/> and
  // the Drawer, so every route change goes through them — not just Back
  // (#10668). Back therefore navigates plainly and the guard does the rest.
  useNavigationGuard();

  const handleBackClick = () => {
    void navigate(-1);
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  // The sticky app bar renders 65px tall: one 32px (h-8) control axis + 32px
  // padding + 1px rule. The single axis is a geometry contract with the
  // Drawer's same-spot top row (see Header.tsx) - if the bar grows, update
  // this constant or the main area clips behind the fold.
  const HEADER_HEIGHT_PX = 65;
  const mainHeight = `calc(100vh - ${HEADER_HEIGHT_PX}px)`;

  return (
    <>
      <StickyComponent className="bg-white">
        <Header onBack={handleBackClick} onMenuToggle={toggleDrawer} />
        <ToastContainer />
      </StickyComponent>
      <div className="bg-white">
        <main
          className="relative mx-auto overflow-auto p-4"
          style={{ height: mainHeight }}
        >
          <Outlet />
        </main>
        <Drawer isOpen={isDrawerOpen} onClose={toggleDrawer} />
      </div>
    </>
  );
}
